import React, { type ComponentPropsWithoutRef, type ComponentRef } from 'react'

import * as SelectPrimitive from '@radix-ui/react-select'

import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { cn } from '@/lib/utils.ts'

export const Select = SelectPrimitive.Root

export const SelectGroup = SelectPrimitive.Group

export const SelectValue = SelectPrimitive.Value

export const SelectLabel = React.forwardRef<
  ComponentRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-4 py-1.5 text-xs font-semibold text-gray-500', className)}
    {...props}
  />
))

interface SelectTriggerProps extends ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  variant?: 'default' | 'highlight' | 'field'
}

export const SelectTrigger = React.forwardRef<ComponentRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps>(
  ({ className, children, variant = 'default', ...props }, ref) => {
    const baseClasses =
      'flex w-full select-none items-center justify-between gap-1 py-1 text-base focus:outline-hidden focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50'

    const variantClasses = {
      default: 'rounded-corner border-b border-gray-300 bg-gray-100 px-3 hover:border-blue-400 hover:bg-blue-200',
      highlight:
        'rounded-corner border-b border-green-400 bg-green-400/10 px-3 hover:border-green-500 hover:bg-green-400/20',
      field: 'rounded-[4px] border border-gray-300 bg-white px-2 hover:border-gray-400',
    }

    return (
      <SelectPrimitive.Trigger ref={ref} className={cn(baseClasses, variantClasses[variant], className)} {...props}>
        {children}
        <SelectPrimitive.Icon asChild>
          <Icons.chevronDown className="size-5 stroke-gray-800" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    )
  }
)

export const SelectContent = React.forwardRef<
  ComponentRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-32 overflow-hidden rounded-[8px] bg-white text-gray-700 shadow-xl',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}>
      <SelectPrimitive.Viewport
        className={cn(
          'max-h-80 overflow-y-auto p-0',
          position === 'popper' && 'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)'
        )}>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))

export const SelectItem = React.forwardRef<
  ComponentRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer items-center px-4 py-2 text-base leading-[150%] outline-hidden select-none focus:bg-blue-100 data-disabled:pointer-events-none data-disabled:opacity-50',
      className
    )}
    {...props}>
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator />
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))

export interface Dropdown<T = string> {
  placeholder: string
  options: Array<{ text: string; value: T; default?: boolean }>
}
